import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Share, View, type TextInputSelectionChangeEvent } from 'react-native';
import { useRoute, type RouteProp, useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useNav, type AllParams } from '../navigation';
import { useHeader } from '../hooks';
import { useAccount } from '../store/account';
import { useFriends } from '../store/friends';
import { useSocial } from '../store/social';
import { useLibrary, newId } from '../store/library';
import * as social from '../platform/social';
import { isVisible, onVisibility } from '../platform/cloud';
import { foregroundPoll } from '../logic/foregroundPoll';
import {
  pickAttachment,
  remotePhotoData,
  localMediaData,
  releasePickedPhoto,
  type PickedPhoto,
} from '../platform/media';
import { feedback } from '../platform/haptics';
import { copyText } from '../platform/clipboard';
import { exportText } from '../platform/files';
import { buildShare, parseShare, adoptShare } from '../logic/share';
import { confirm, notify, showMenu } from '../components/Dialogs';
import { Body, Button, Caption, Field, IconButton, ListItem, Row, Screen, Section } from '../components/ui';
import { RecipePickerSheet } from '../components/Pickers';
import Markdown from '../components/Markdown';
import MediaAttachment from '../platform/MediaAttachment';
import { spacing, usePalette } from '../theme';
import type { FriendPost, Recipe } from '../types';

function FriendsHeaderAction({ filter, clear }: { filter?: string; clear?: () => void }) {
  const nav=useNav();
  const {t}=useTranslation();
  return <Row>{filter ? <><Caption>{filter}</Caption><IconButton icon="close" label={t('common.close')} onPress={() => clear?.()} /></> : null}<IconButton icon="settings" label={t('settings.title')} onPress={()=>nav.navigate('Settings')} /></Row>;
}

export default function FriendsScreen() {
  const { t } = useTranslation(),
    nav = useNav(),
    p = usePalette();
  const linkedFriend = useRoute<RouteProp<AllParams, 'SocialHome'>>().params?.friendId;
  const openedFriend = useRef<string | undefined>(undefined);
  const user = useAccount(s => s.user);
  const accountError = useAccount(s => s.error);
  const state = useFriends();
  const offline = !user && accountError === 'generic' && !!state.owner;
  const overview = state.owner === user?.id || offline ? state.overview : null;
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    if (!linkedFriend || openedFriend.current === linkedFriend) return;
    const found = overview?.threads.find(thread => thread.friend.userId === linkedFriend);
    if (found) {
      openedFriend.current = linkedFriend;
      setSelected(found.id);
    }
  }, [linkedFriend, overview]);
  const [caption, setCaption] = useState('');
  const [captionSelection, setCaptionSelection] = useState({ start: 0, end: 0 });
  const [photo, setPhoto] = useState<PickedPhoto | null>(null);
  useEffect(
    () => () => {
      if (photo) releasePickedPhoto(photo);
    },
    [photo],
  );
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [picking, setPicking] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setCaption('');
    setPhoto(null);
    setRecipe(null);
    setSelected(null);
    setReplyTo(null);
    setReply('');
  }, [user?.id]);
  const [next, setNext] = useState<number | null>(null);
  const errorMessage = (code: string) =>
    code === 'archive_full'
      ? t('relay.archiveFull')
      : code === 'super_required'
      ? t('relay.archiveError')
      : ['media_too_large', 'unsupported_image', 'unsupported_media', 'too_large'].includes(code)
      ? t('relay.mediaLimit')
      : t('relay.failed');
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      feedback('error');
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const refreshing = useRef(false);
  const refreshEpoch = useRef(0);
  const refresh = useCallback(async () => {
    if (!user || refreshing.current || !isVisible()) return;
    refreshing.current = true;
    const epoch = refreshEpoch.current;
    const active = () => epoch === refreshEpoch.current && isVisible() && useAccount.getState().user?.id === user.id;
    try {
      await useFriends.getState().refresh();
      if (!active()) return;
      await useFriends.getState().flush();
      if (!active()) return;
      await useFriends.getState().sync();
      if (!active()) return;
      if (selected) {
        const nextPage = await useFriends.getState().load(selected);
        if (active()) setNext(nextPage);
      }
    } finally { refreshing.current = false; }
  }, [user?.id, selected]); // eslint-disable-line react-hooks/exhaustive-deps
  useFocusEffect(
    useCallback(() => {
      const stop = foregroundPoll(refresh, isVisible, onVisibility);
      return () => { refreshEpoch.current++; stop(); };
    }, [refresh]),
  );
  useHeader({title:'',headerRight: () => <FriendsHeaderAction filter={overview?.threads.find(thread => thread.id === selected)?.friend.displayName} clear={() => setSelected(null)} />},[selected, overview]);
  const activeThread = overview?.threads.find(x => x.id === selected);
  const savedThreads = Object.keys(state.posts).filter(
    id => state.posts[id].length && !overview?.threads.some(thread => thread.id === id),
  );
  const current =
    activeThread ??
    (selected && state.posts[selected]?.length
      ? { id: selected, friend: { displayName: state.names[selected] ?? t('relay.savedConversations') } }
      : undefined);
  const invite = () =>
    run(async () => {
      const link = await social.friendLink();
      try {
        await Share.share({ message: t('friends.inviteMessage', { url: link.url }) });
      } catch (e) {
        if ((e as { name?: string }).name !== 'AbortError') {
          await copyText(link.url);
          notify(t('streaks.linkCopied'));
        }
      }
    });
  const exportLegacy = () =>
    run(async () => {
      const deviceArchive = useSocial.getState().owner === user?.id ? useSocial.getState().feeds : {};
      const devicePosts = new Map(
        Object.values(deviceArchive)
          .flatMap(feed => feed.posts)
          .map(post => [post.id, post]),
      );
      const seen = new Set<string>();
      let after: string | undefined,
        part = 1,
        records: string[] = [],
        size = 0;
      const flush = async () => {
        if (!records.length) return true;
        const text = `{"format":"chefi-legacy-groups","version":2,"part":${part},"exportedAt":${JSON.stringify(
          new Date().toISOString(),
        )},"records":[${records.join(',')}]}`;
        const saved = await exportText(`chefi-legacy-groups-${part++}.json`, text);
        records = [];
        size = 0;
        return saved;
      };
      const append = async (record: unknown) => {
        const text = JSON.stringify(record);
        if (size + text.length > 16 * 1024 * 1024 && !(await flush())) return false;
        records.push(text);
        size += text.length;
        return true;
      };
      do {
        const page = await social.friendLegacy(after);
        for (const post of page.posts) {
          let media: string | undefined,
            mediaUnavailable = false;
          if (post.photo) {
            try {
              media = await remotePhotoData(social.photoSource(post.id), post.mediaType);
            } catch (e) {
              if (['media_download_404', 'media_download_410'].includes((e as Error).message)) mediaUnavailable = true;
              else throw e;
            }
          }
          if (
            !(await append({
              kind: 'post',
              post: {
                ...devicePosts.get(post.id),
                ...post,
                media,
                ...(mediaUnavailable ? { mediaUnavailable: true } : {}),
              },
            }))
          )
            return;
          seen.add(post.id);
        }
        for (const legacyReply of page.replies) if (!(await append({ kind: 'reply', reply: legacyReply }))) return;
        after = page.next ?? undefined;
      } while (after);
      for (const post of devicePosts.values())
        if (!seen.has(post.id) && !(await append({ kind: 'post', post, source: 'device' }))) return;
      if (!records.length && part === 1) records.push(JSON.stringify({ kind: 'empty-archive' }));
      if (!(await flush())) return;
      if (!(await confirm(t('relay.start'), t('relay.savedBackup'), t('common.done'), false))) return;
      await social.friendStart();
      await state.refresh();
    });
  const exportConversation = (threadId: string) =>
    run(async () => {
      let part = 1,
        records: string[] = [],
        bytes = 0;
      const savePart = async () => {
        if (!records.length) return true;
        const text = `{"format":"chefi-friend-archive","version":1,"owner":${JSON.stringify(
          overview?.me,
        )},"threadId":${JSON.stringify(threadId)},"part":${part},"posts":[${records.join(',')}]}`;
        const saved = await exportText(`chefi-conversation-${part++}.json`, text);
        records = [];
        bytes = 0;
        return saved;
      };
      for (const post of state.posts[threadId] ?? []) {
        const { localMedia, ...text } = post;
        const record = JSON.stringify({ ...text, media: localMedia ? await localMediaData(localMedia) : undefined });
        if (bytes + record.length > 16 * 1024 * 1024 && !(await savePart())) return;
        records.push(record);
        bytes += record.length;
      }
      await savePart();
    });
  const postMenu = (post: FriendPost) =>
    selected && !activeThread
      ? showMenu([
          { label: t('relay.exportConversation'), icon: 'export', onPress: () => exportConversation(post.threadId) },
        ])
      : showMenu([
          ...(post.authorId === overview?.me
            ? [
                {
                  label: t('common.delete'),
                  icon: 'trash' as const,
                  destructive: true,
                  onPress: () =>
                    run(async () => {
                      if (await confirm(t('social.deletePost'), undefined)) {
                        await social.friendDelete(post.id);
                        await state.load(post.threadId);
                      }
                    }),
                },
              ]
            : [
                { label: t('relay.reply'), icon: 'buddies' as const, onPress: () => setReplyTo(post.id) },
                {
                  label: t('social.report'),
                  icon: 'report' as const,
                  onPress: () =>
                    run(async () => {
                      await social.report('post', post.id, 'inappropriate');
                      notify(t('social.reported'));
                    }),
                },
              ]),
        ]);
  const posts = !overview ? [] : selected ? state.posts[selected] ?? [] : Object.values(state.posts).flat().filter((post, index, all) => all.findIndex(other => other.authorId === post.authorId && other.createdAt === post.createdAt && other.caption === post.caption) === index).sort((a, b) => a.createdAt - b.createdAt);
  if (!user && !offline)
    return (
      <Screen>
        <Button label={t('account.title')} icon="user" onPress={() => nav.navigate('Account')} />
      </Screen>
    );
  return (
    <Screen>
      {offline ? <Caption>{t('relay.offline')}</Caption> : null}
      {error || state.error ? <Caption color={p.danger}>{errorMessage(error ?? state.error!)}</Caption> : null}
      {!overview ? (
        <Button label={t('relay.retry')} onPress={() => refresh()} />
      ) : !overview.started ? (
        <Section>
          <Body>{t('relay.exportFirst')}</Body>
          {overview.legacyUntil ? (
            <Caption>{t('relay.exportUntil', { date: new Date(overview.legacyUntil).toLocaleDateString() })}</Caption>
          ) : null}
          <Button
            label={overview.legacyCount ? t('relay.exportStart') : t('relay.start')}
            icon="export"
            loading={busy}
            onPress={
              overview.legacyCount
                ? exportLegacy
                : () =>
                    run(async () => {
                      await social.friendStart();
                      await state.refresh();
                    })
            }
          />
        </Section>
      ) : (
        <>
          <Row>
            <IconButton label={t('friends.invite')} icon="plus" onPress={invite} />
            <IconButton icon="reset" label={t('relay.retry')} onPress={() => refresh()} />
            {savedThreads.length ? (
              <IconButton
                icon="library"
                label={t('relay.savedConversations')}
                onPress={() =>
                  showMenu(
                    savedThreads.map(id => ({
                      label: state.names[id] ?? t('relay.savedConversations'),
                      icon: 'buddies',
                      onPress: () => setSelected(id),
                    })),
                  )
                }
              />
            ) : null}
          </Row>
          <Section>
            {overview.publicHandle ? (
              <Caption>{t('relay.publicPost', { handle: overview.publicHandle })}</Caption>
            ) : null}
            <Field
              value={caption}
              onChangeText={setCaption}
              onSelectionChange={(event: TextInputSelectionChangeEvent) => setCaptionSelection(event.nativeEvent.selection)}
              multiline
              maxLength={2000}
              placeholder={t('social.composer')}
            />
            <Row>
              {([['**', 'Bold'], ['*', 'Italic'], ['# ', 'Heading']] as const).map(([mark, label]) => (
                <Button key={label} label={label} variant="secondary" onPress={() => {
                  const { start, end } = captionSelection;
                  const selected = caption.slice(start, end) || label;
                  const next = `${caption.slice(0, start)}${mark}${selected}${mark === '# ' ? '' : mark}${caption.slice(end)}`;
                  setCaption(next);
                  setCaptionSelection({ start: start + mark.length, end: start + mark.length + selected.length });
                }} />
              ))}
            </Row>
            {photo ? (
              <MediaAttachment
                media={{ uri: photo.uri, mimeType: photo.mimeType }}
                seed="draft"
                style={{ width: '100%', aspectRatio: 4 / 3 }}
              />
            ) : null}
            {recipe ? <Caption>{recipe.title}</Caption> : null}
            <Row>
              <Button
                label={t('relay.addMedia')}
                iconOnly
                icon="camera"
                variant="secondary"
                onPress={() =>
                  run(async () => {
                    const picked = await pickAttachment();
                    if (picked) {
                      setPhoto(picked);
                      feedback('selection');
                    }
                  })
                }
              />
              {photo ? (
                <IconButton icon="close" label={t('social.removeAttachment')} onPress={() => setPhoto(null)} />
              ) : null}
              <Button
                label={t('social.addRecipe')}
                iconOnly
                icon="recipe"
                variant="secondary"
                onPress={() => setPicking(true)}
              />
            </Row>
            <Button
              label={t('social.post')}
              icon="send"
              loading={busy || state.busy}
              disabled={(!overview.threads.length && !overview.publicHandle) || (!caption.trim() && !photo && !recipe)}
              onPress={() =>
                run(async () => {
                  await state.send(caption.trim(), recipe ? buildShare(recipe, useLibrary.getState()) : null, photo);
                  feedback('success');
                  setCaption(value => (value === caption ? '' : value));
                  setRecipe(value => (value === recipe ? null : value)); // Keep the selected photo in place after saving.
                })
              }
            />
            {state.outbox.length ? (
              <Button
                label={t('relay.retryOutbox', { count: state.outbox.length })}
                icon="reset"
                onPress={() => state.flush()}
              />
            ) : null}
          </Section>
          <Section>
            {!overview.threads.length ? <Body secondary>{t('friends.none')}</Body> : null}
            {overview.threads.map(friend => {
              const latestPost = state.posts[friend.id]?.at(-1);
              const streak = friend.count ? `${friend.broken ? '◌' : '🔥'} ${friend.count}` : '';
              return <ListItem
                key={friend.id}
                title={friend.friend.displayName}
                icon="buddies"
                subtitle={[latestPost ? `• ${latestPost.caption.slice(0, 42) || latestPost.authorName}` : '', streak].filter(Boolean).join('  ·  ') || undefined}
                onPress={() => {
                  setSelected(value => value === friend.id ? null : friend.id);
                  setReplyTo(null);
                }}
                right={
                  <IconButton
                    icon="more"
                    label={t('common.more')}
                    onPress={() =>
                      showMenu([
                        {
                          label: t('relay.exportConversation'),
                          icon: 'export',
                          onPress: () => exportConversation(friend.id),
                        },
                        ...(friend.canRestore
                          ? [
                              {
                                label: t('relay.restore'),
                                icon: 'reset' as const,
                                onPress: () =>
                                  run(async () => {
                                    await social.friendRestore(friend.id);
                                    feedback('success');
                                    await state.refresh();
                                  }),
                              },
                            ]
                          : []),
                        {
                          label: t('friends.remove'),
                          icon: 'trash',
                          destructive: true,
                          onPress: () =>
                            run(async () => {
                              if (await confirm(t('friends.remove'), t('relay.removeFriend'))) {
                                await social.removeFriend(friend.friend.userId);
                                setSelected(null);
                                await state.refresh();
                              }
                            }),
                        },
                      ])
                    }
                  />
                }
              />;
            })}
          </Section>
          {current || posts.length ? (
            <Section>
              {current && !activeThread ? <Caption>{current.friend.displayName}</Caption> : null}
              {next && current ? (
                <Button
                  label={t('relay.older')}
                  variant="secondary"
                  onPress={() =>
                    run(async () => {
                      setNext(await state.load(current.id, next));
                    })
                  }
                />
              ) : null}
              {posts.map(post => (
                <View key={post.id} style={{ width: '100%', alignItems: post.authorId === overview?.me ? 'flex-end' : 'flex-start' }}>
                <View style={{ width: '88%', backgroundColor: post.authorId === overview?.me ? p.tint : p.card, borderRadius: 18, padding: spacing.m, gap: spacing.s }}>
                  <Row>
                    <Caption color={post.authorId === overview?.me ? p.onTint : undefined}>
                      {post.authorName} · {new Date(post.createdAt).toLocaleString()}
                    </Caption>
                    <IconButton icon="more" label={t('common.more')} onPress={() => postMenu(post)} />
                  </Row>
                  {post.replyTo ? <Caption color={post.authorId === overview?.me ? p.onTint : undefined}>{t('relay.reply')}</Caption> : null}
                  <Markdown color={post.authorId === overview?.me ? p.onTint : undefined}>{post.caption}</Markdown>
                  {post.localMedia ? (
                    <MediaAttachment
                      media={post.localMedia}
                      seed={post.id}
                      style={{ width: '100%', aspectRatio: 4 / 3 }}
                    />
                  ) : null}
                  {parseShare(post.recipe) ? (
                    <Button
                      label={t('path.saveToLibrary')}
                      icon="import"
                      variant="secondary"
                      onPress={() => {
                        const share = parseShare(post.recipe)!;
                        useLibrary
                          .getState()
                          .importItems(adoptShare(share, useLibrary.getState(), newId(), post.authorName, Date.now()));
                        notify(t('path.savedToLibrary'));
                      }}
                    />
                  ) : null}
                  {replyTo === post.id ? (
                    <>
                      <Field
                        value={reply}
                        onChangeText={setReply}
                        multiline
                        maxLength={2000}
                        placeholder={t('relay.reply')}
                      />
                      <Button
                        label={t('relay.reply')}
                        icon="send"
                        disabled={!reply.trim() || busy}
                        onPress={() =>
                          run(async () => {
                            await state.reply(post.id, reply);
                            feedback('success');
                            setReply(value => (value === reply ? '' : value));
                            setReplyTo(value => (value === post.id ? null : value));
                            await state.load(post.threadId);
                            await state.refresh();
                          })
                        }
                      />
                    </>
                  ) : null}
                </View>
                </View>
              ))}
            </Section>
          ) : null}
        </>
      )}
      <RecipePickerSheet
        visible={picking}
        onClose={() => setPicking(false)}
        onPick={r => {
          setRecipe(r);
          setPicking(false);
        }}
      />
    </Screen>
  );
}
